/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './TimelineConnector';

const TimelineConnectorManager: ComponentManagerFn<'TimelineConnector'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'TimelineConnector'>(platform, 'TimelineConnector'),
    type: 'TimelineConnector',
    name: 'Timeline Connector',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      color: {
        label: 'Color',
        type: 'ColorExpression',
      },
    },
    getDisplayText: (item) => 'Timeline Connector',
    category: 'Forms',
    icon: { iconName: 'grip-lines-vertical' },
    description: `**Overview**<br/>
The Timeline Connector is a purely visual structural component used to build **Timeline** visualizations. It renders the vertical line that connects one event (dot) in a timeline to the next, creating the appearance of a continuous path or sequence.
Use this component **only** as a child of a **Timeline** or **TimelineItem** structure to visually link steps in a process.

**1. Configuration**<br/>
The component is simple and focused on styling.
* **Color:** Defines the color of the vertical line.
* **Usage:** Use this to indicate status—for example, a "Green" line might connect completed steps, while a "Grey" line connects pending steps.

**2. Interactivity**<br/>
* **TriggerOnClick:** While primarily decorative, you can attach a trigger to it. This allows the connector line itself to be clickable.
* **Functionality:** Clicking the line between two milestones (e.g., "Order Placed" and "Shipped") could be configured to show details about the transit time or intermediate logs.

**3. Best Use Cases**<br/>
* **Order Tracking:** Drawing the line between "Ordered", "Shipped", and "Delivered" milestones to show progress.
* **History Logs:** Connecting sequential audit log entries to show a chronological trail.
* **Process Steps:** Visualizing a multi-stage approval workflow where the line indicates the flow of authority.`,
  };
};

export default TimelineConnectorManager;
